import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import ContactForm from './ContactForm';
import ChatbotAgent from './ChatbotAgent';
import ParticleBackground from './ParticleBackground';
import PortfolioSection from './PortfolioSection';
import {
  certifications,
  education,
  experience,
  personalInfo,
  projects,
  skills
} from '../data/resumeData';
import {
  Briefcase,
  Bot,
  Cloud,
  GitBranch,
  Terminal,
  Code,
  LineChart,
  CheckCircle,
  Clock,
  Download,
  FolderKanban,
  Star,
  GraduationCap,
  Mail,
  Github,
  Linkedin,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun,
  MapPin,
  Phone,
  Youtube,
  Calendar,
  ExternalLink,
  Target,
  Award,
  X
} from 'lucide-react';

const ModernResume = () => {
  const [activeSection, setActiveSection] = useState('experience');
  const [expandedItems, setExpandedItems] = useState({ exp1: true });
  const [darkMode, setDarkMode] = useState(true);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const getSkillIcon = (category) => {
    const iconClass = "w-5 h-5 text-blue-600 dark:text-blue-400";
    switch (category) {
      case 'Cloud & Infrastructure':
        return <Cloud className={iconClass} />;
      case 'CI/CD & Automation':
        return <GitBranch className={iconClass} />;
      case 'AI & LLMOps':
        return <Bot className={iconClass} />;
      case 'Containers & Systems':
        return <Terminal className={iconClass} />;
      case 'Scripting & Programming':
        return <Code className={iconClass} />;
      case 'Monitoring & Data':
        return <LineChart className={iconClass} />;
      default:
        return <Code className={iconClass} />;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
  
    const skillsSection = document.querySelector('.skills-section');
  
    if (skillsSection) {
      observer.observe(skillsSection);
    }
  
    return () => {
      if (skillsSection) {
        observer.unobserve(skillsSection); // Ensure observer stops tracking before component unmounts
      }
      observer.disconnect();
    };
  }, []);
  

  const toggleItem = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
    return (
      <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      {/* Theme Togglle */}
      <button
        onClick={() => setDarkMode(!darkMode)}
        className="fixed top-4 right-4 z-50 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg"
        aria-label="Toggle theme"
      >
        {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Hero Section */}
      <div className="hero-background min-h-[50vh] relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-gray-900/90 animate-gradient" />
        <ParticleBackground />

        <div className="container mx-auto px-4 relative z-10">
          <div className="hero-content mt-8">
            {/* Profile Section */}
            <div className="text-center">
              {/* Profile Image with status */}
              <div className="relative w-40 h-40 mx-auto mb-4 group">
                {/* Background glow effect */}
                <div className="absolute inset-0 rounded-full bg-blue-500/30 blur-xl animate-pulse-slow" />

                {/* Image container with border */}
                <div className="relative w-full h-full rounded-full overflow-hidden border-4 border-blue-500 shadow-lg 
      transform transition-all duration-500 group-hover:scale-105 bg-blue-900">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/profile-photo.jpg`}
                    alt={personalInfo.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${process.env.PUBLIC_URL}/images/fallback-avatar.png`;
                    }}
                  />
                </div>

                {/* Online status indicator */}
                <div className="absolute bottom-0 right-0 transform translate-x-1 translate-y-1">
                  <div className="relative">
                    <div className="w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-900">
                      <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75"></div>
                    </div>
                    {/* Status tooltip */}
                    <div className="absolute -top-8 right-0 w-24 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-black/80 text-white text-xs py-1 px-2 rounded-md whitespace-nowrap">
                        Available for work
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Name and professional title */}
              <div className="space-y-2 mb-4">
                <h1 className="text-4xl font-bold text-white">{personalInfo.name}</h1>
                <p className="text-xl text-gray-300 animate-fadeIn delay-300">{personalInfo.title}</p>
              </div>

              {/* Description with animated background */}
              <div className="max-w-2xl mx-auto mb-6 relative group">
                <p className="text-gray-300 leading-relaxed relative z-10 p-4 rounded-lg
            bg-white/5 backdrop-blur-sm border border-white/10 transform transition-all duration-300
            group-hover:scale-105">
                  {personalInfo.description}
                </p>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg
            blur-xl transition-all duration-300 group-hover:scale-110" />
              </div>

              {/* Contact Info with hover cards */}
              <div className="flex flex-wrap justify-center gap-6 mb-6">
                {[
                  { icon: Mail, text: personalInfo.email, href: `mailto:${personalInfo.email}`, type: 'email' },
                  { icon: Phone, text: personalInfo.phone, copy: true, type: 'phone' },
                  { icon: MapPin, text: personalInfo.location, type: 'location' }
                ].map((contact) => (
                  <div key={contact.type} className="relative group">
                    {contact.href ? (
                      <a
                        href={contact.href}
                        className="contact-card"
                      >
                        <contact.icon className="w-5 h-5" />
                        <span>{contact.text}</span>
                      </a>
                    ) : (
                      <div
                        className="contact-card cursor-pointer"
                        onClick={() => contact.copy && navigator.clipboard.writeText(contact.text)}
                      >
                        <contact.icon className="w-5 h-5" />
                        <span>{contact.text}</span>
                      </div>
                    )}
                    <div className="tooltip">
                      {contact.copy ? 'Click to copy' : contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links with 3D cards */}
              <div className="flex justify-center space-x-8 mb-4">
                {[
                  { icon: Linkedin, href: personalInfo.social.linkedin, label: 'LinkedIn', color: 'blue' },
                  { icon: Github, href: personalInfo.social.github, label: 'GitHub', color: 'gray' },
                  { icon: Youtube, href: personalInfo.social.youtube, label: 'YouTube', color: 'red' }
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-card-3d"
                  >
                    <div className="social-card-content">
                      <social.icon className="w-7 h-7" />
                      <span className="social-tooltip">{social.label}</span>
                    </div>
                    <div className="social-card-shine" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="bg-white dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4">
          {/* Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {['experience', 'portfolio', 'skills', 'education', 'certifications', 'contact'].map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-300
      ${activeSection === section
                    ? 'bg-blue-600 text-white scale-105 shadow-lg'
                    : 'bg-blue-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-300'}`}
              >
                {section === 'experience' && <Briefcase className="w-5 h-5 mr-2" />}
                {section === 'portfolio' && <FolderKanban className="w-5 h-5 mr-2" />}
                {section === 'skills' && <Code className="w-5 h-5 mr-2" />}
                {section === 'education' && <GraduationCap className="w-5 h-5 mr-2" />}
                {section === 'certifications' && <Award className="w-5 h-5 mr-2" />}
                {section === 'contact' && <Mail className="w-5 h-5 mr-2" />}
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
            <a
              href={`${process.env.PUBLIC_URL}/Alphonse-Ngandjui-Resume.pdf`}
              download="Alphonse-Ngandjui-Resume.pdf"
              className="flex items-center px-6 py-3 rounded-lg font-medium bg-green-600 text-white shadow-lg transition-all duration-300 hover:bg-green-700 hover:scale-105"
            >
              <Download className="w-5 h-5 mr-2" />
              Download PDF
            </a>
          </div>

          {/* Content Sections */}
          <div className="max-w-4xl mx-auto">
            {/* Experience Section */}
            {activeSection === 'experience' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6 animate-slideInRight">
                  <div className="relative">
                    <Briefcase className="w-6 h-6 mr-2 text-blue-600" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
                  </div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                    Professional Experience
                  </h2>
                </div>

                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-900" />

                  {experience.map((exp, index) => (
                    <Card
                      key={exp.id}
                      className={`transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 
            mb-8 relative z-10 ${darkMode ? 'dark:bg-gray-800/90' : 'bg-white/90'}
            backdrop-blur-sm border border-blue-100 dark:border-blue-900`}
                    >
                      {/* Timeline dot */}
                      <div className="absolute -left-3 top-8 w-6 h-6 rounded-full border-4 border-blue-500 bg-white dark:bg-gray-800" />

                      <CardContent className="p-6 ml-10">
                        <div
                          className="cursor-pointer group"
                          onClick={() => toggleItem(exp.id)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="space-y-2 flex-grow">
                              <div className="flex items-center space-x-4">
                                <h3 className="text-xl font-semibold text-blue-600 dark:text-blue-400">
                                  {exp.title}
                                </h3>
                                <a
                                  href={exp.companyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative group inline-flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900 
                        transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                                    <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1 
                        bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 
                        transition-all duration-300 whitespace-nowrap">
                                    Verify Company
                                  </span>
                                </a>
                              </div>

                              <div className="text-lg text-gray-700 dark:text-gray-300 font-medium">
                                {exp.company}
                              </div>

                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                                  <Calendar className="w-4 h-4 mr-2" />
                                  {exp.period}
                                </span>
                                <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                                  <MapPin className="w-4 h-4 mr-2" />
                                  {exp.location}
                                </span>
                              </div>
                            </div>

                            <button
                              className="transform transition-transform duration-300 group-hover:rotate-180
                    p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                              aria-label={expandedItems[exp.id] ? 'Collapse details' : 'Expand details'}
                            >
                              {expandedItems[exp.id] ?
                                <ChevronUp className="w-5 h-5" /> :
                                <ChevronDown className="w-5 h-5" />
                              }
                            </button>
                          </div>
                        </div>

                        {expandedItems[exp.id] && (
                          <div className="mt-6 space-y-6 animate-fadeIn">
                            <div className="prose dark:prose-invert max-w-none">
                              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {exp.description}
                              </p>
                            </div>

                            <div className="space-y-4">
                              <h4 className="font-semibold text-blue-600 dark:text-blue-400 flex items-center">
                                <Target className="w-4 h-4 mr-2" />
                                Key Achievements
                              </h4>
                              <ul className="space-y-3">
                                {exp.achievements.map((achievement, i) => (
                                  <li
                                    key={i}
                                    className="flex items-start text-gray-700 dark:text-gray-300 group
                          transform transition-all duration-300 hover:translate-x-2"
                                  >
                                    <div className="mt-1.5 mr-3 w-2 h-2 rounded-full bg-blue-500 
                          group-hover:bg-blue-600 transition-colors duration-200" />
                                    <span className="flex-1">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div className="space-y-4">
                              <h4 className="font-semibold text-blue-600 dark:text-blue-400 flex items-center">
                                <Code className="w-4 h-4 mr-2" />
                                Technologies & Tools
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {exp.technologies.map((tech, i) => (
                                  <span
                                    key={i}
                                    className="px-4 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-800 
                          dark:text-blue-200 rounded-full text-sm hover:scale-105 
                          transition-all duration-200 cursor-default
                          hover:shadow-md hover:bg-blue-200 dark:hover:bg-blue-800"
                                  >
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {activeSection === 'portfolio' && (
              <PortfolioSection projects={projects} />
            )}

            {/* Skills Section */}
            {activeSection === 'skills' && (
              <div className="space-y-8">
                <div className="flex items-center mb-6 animate-slideInRight">
                  <div className={`skills-section space-y-8 ${isVisible ? 'is-visible' : ''}`}>
                    <div className="relative">
                      <Code className="w-6 h-6 mr-2 text-blue-600" />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-ping" />
                    </div>

                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                      Technical Skills
                    </h2>
                  </div>
                </div>

                <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  {skills.map((skillGroup, skillGroupIndex) => (
                    skillGroup.items.map((skill, skillIndex) => (
                      <div
                        key={`${skillGroupIndex}-${skillIndex}`}
                        className="progress-bar-animate absolute top-0 left-0 h-full rounded-full
          bg-gradient-to-r from-blue-500 to-blue-400"
                        style={{
                          '--final-width': `${skillGroup.proficiency[skillIndex]}%`,
                          width: `${skillGroup.proficiency[skillIndex]}%`
                        }}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-shimmer" />
                      </div>
                    ))
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {skills.map((skillGroup, index) => (
                    <Card
                      key={index}
                      className={`transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1
            ${darkMode ? 'dark:bg-gray-800/90' : 'bg-white/90'}
            backdrop-blur-sm border border-blue-100 dark:border-blue-900`}
                    >
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                              {getSkillIcon(skillGroup.category)}
                            </div>
                            <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                              {skillGroup.category}
                            </h3>
                          </div>

                          <div className="space-y-4">
                            {skillGroup.items.map((skill, skillIndex) => (
                              <div
                                key={skillIndex}
                                onClick={() => setSelectedSkill({
                                  skill,
                                  category: skillGroup.category,
                                  proficiency: skillGroup.proficiency[skillIndex]
                                })}
                                className="group hover:scale-105 transition-all duration-300 cursor-pointer"
                              >
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-gray-700 dark:text-gray-300 group-hover:text-blue-500 
                        transition-colors duration-300">
                                    {skill}
                                  </span>
                                  <span className={`text-sm text-gray-500 dark:text-gray-400 opacity-0 
  ${isVisible ? 'animate-fadeIn' : ''}`}
                                    style={{ animationDelay: `${skillIndex * 0.1 + 0.5}s` }}
                                  >
                                    {skillGroup.proficiency[skillIndex]}%
                                  </span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {skillGroup.proficiency[skillIndex]}%
                                  </span>
                                </div>
                                <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-1000
                          bg-gradient-to-r from-blue-500 to-blue-400"
                                    style={{ width: `${skillGroup.proficiency[skillIndex]}%` }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {selectedSkill && (
                  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 relative animate-fadeIn">
                      <button
                        onClick={() => setSelectedSkill(null)}
                        className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <X className="w-5 h-5" />
                      </button>

                      <div className="flex items-center space-x-3 mb-4">
                        {getSkillIcon(selectedSkill.category)}
                        <h3 className="text-xl font-semibold">{selectedSkill.skill}</h3>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Star className="w-5 h-5 text-yellow-500" />
                          <span className="text-lg">{selectedSkill.proficiency}% Proficiency</span>
                        </div>

                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                            style={{ width: `${selectedSkill.proficiency}%` }}
                          />
                        </div>

                        <div className="pt-4 border-t dark:border-gray-700">
                          <h4 className="font-semibold mb-2">Experience Level</h4>
                          <div className="flex items-center space-x-2">
                            {selectedSkill.proficiency >= 90 ? (
                              <Award className="w-5 h-5 text-blue-500" />
                            ) : selectedSkill.proficiency >= 80 ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <Clock className="w-5 h-5 text-yellow-500" />
                            )}
                            <span>
                              {selectedSkill.proficiency >= 90 ? 'Expert' :
                                selectedSkill.proficiency >= 80 ? 'Advanced' : 'Intermediate'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Education Section */}
            {activeSection === 'education' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6 animate-slideInRight">
                  <div className="relative">
                    <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-ping" />
                  </div>
                  <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">
                    Education
                  </h2>
                </div>

                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200 dark:bg-blue-900" />

                  {education.map((edu, index) => (
                    <Card
                      key={edu.id}
                      className={`transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1 
            mb-8 relative z-10 ${darkMode ? 'dark:bg-gray-800/90' : 'bg-white/90'}
            backdrop-blur-sm border border-blue-100 dark:border-blue-900`}
                    >
                      {/* Timeline dot */}
                      <div className="absolute -left-3 top-8 w-6 h-6 rounded-full border-4 border-blue-500 bg-white dark:bg-gray-800 timeline-dot" />

                      <CardContent className="p-6 ml-10">
                        <div className="group">
                          <div className="flex justify-between items-start">
                            <div className="space-y-3">
                              <div className="flex items-center space-x-4">
                                <h3 className="text-xl font-semibold text-blue-600 dark:text-blue-400">
                                  {edu.degree}
                                </h3>
                                <a
                                  href="https://www.univ-douala.cm/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative group inline-flex items-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900 
                        transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                                    <ExternalLink className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1 
                        bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 
                        transition-all duration-300 whitespace-nowrap">
                                    Visit University Website
                                  </span>
                                </a>
                              </div>

                              <div className="text-lg text-gray-700 dark:text-gray-300 font-medium group-hover:text-blue-500 transition-colors duration-300">
                                {edu.school}
                              </div>

                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                                  <Calendar className="w-4 h-4 mr-2" />
                                  {edu.period}
                                </span>
                                <span className="flex items-center bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                                  <MapPin className="w-4 h-4 mr-2" />
                                  {edu.location}
                                </span>
                              </div>

                              {/* Additional Features */}
                              <div className="pt-4 flex flex-wrap gap-3">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm
                      bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  <GraduationCap className="w-4 h-4 mr-1" />
                                  {edu.id === 'edu1' ? 'Graduate Studies' : 'Undergraduate Studies'}
                                </span>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm
                      bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  <Calendar className="w-4 h-4 mr-1" />
                                  Full-Time
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            {/* Certifications Section */}
            {activeSection === 'certifications' && (
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <Award className="w-6 h-6 mr-2 text-blue-600" />
                  <h2 className="text-2xl font-bold">Certifications</h2>
                </div>
                {certifications.map((cert) => (
                  <Card
                    key={cert.id}
                    className="transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 dark:bg-gray-800"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2 flex-grow">
                          <div className="flex items-center justify-between">
                            <h3 className="text-xl font-semibold group inline-flex items-center">
                              {cert.name}
                              <div className="relative ml-2">
                                <a
                                  href={cert.verifyUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-blue-500 hover:text-blue-600 transition-all duration-300
                        hover:scale-110 group"
                                >
                                  <div className="p-1 rounded-full bg-blue-100 dark:bg-blue-900">
                                    <ExternalLink className="w-4 h-4" />
                                  </div>
                                  <span className="ml-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    Verify Certificate
                                  </span>
                                  {/* Tooltip */}
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                    Click to verify on Credly
                                  </span>
                                </a>
                              </div>
                            </h3>
                            <Award className="w-8 h-8 text-blue-500 animate-pulse" />
                          </div>
                          <div className="space-y-1">
                            <p className="text-gray-600 dark:text-gray-300 flex items-center">
                              <span className="font-medium">Issuer:</span>
                              <span className="ml-2">{cert.issuer}</span>
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 flex items-center">
                              <span className="font-medium">Issued:</span>
                              <span className="ml-2">{cert.date}</span>
                            </p>
                          </div>
                          {/* Credential Status Badge */}
                          <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                            <div className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-ping" />
                            <span className="text-sm">Active Credential</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {/* Contact Section */}
            {activeSection === 'contact' && (
              <div className="space-y-6">
                <Card className="hover:shadow-lg transition-all duration-300 dark:bg-gray-800">
                  <CardContent className="p-6">
                    <div className="max-w-3xl mx-auto">
                      <ContactForm />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-blue-100 dark:bg-blue-800 py-3">
        <div className="container mx-auto px-4 text-center text-blue-600 dark:text-blue-300 space-y-1">
          <p>© {new Date().getFullYear()} {personalInfo.name}. All rights reserved.</p>
          <p className="text-sm">
            Website developed by{' '}
            <a
              href="https://trouvitsolutions.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4 transition-colors hover:text-blue-800 dark:hover:text-white"
            >
              TrouvIT Solutions
            </a>
          </p>
        </div>
      </footer>
      <ChatbotAgent />
    </div>
  );
};

export default ModernResume;
